if (!customElements.get('product-sticky-bar')) {
  customElements.define(
    'product-sticky-bar',
    class ProductStickyBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.priceTarget = this.querySelector('[data-sticky-price]');
        this.button = this.querySelector('[data-sticky-add]');
        this.buttonText = this.querySelector('[data-sticky-add-text]');
        this.qtyWrap = this.querySelector('[data-sticky-qty-wrap]');
        this.qtyDisplay = this.querySelector('[data-sticky-qty]');
        this.image = this.querySelector('[data-sticky-image]');

        this.inner = this.querySelector('.product-sticky-bar__inner');
        this.scope = document.getElementById(`MainProduct-${this.sectionId}`) || document;
        this.mainPrice = document.getElementById(`price-${this.sectionId}`);

        // Proxy the add to the real button so the theme's own handler runs.
        this.button.addEventListener('click', (event) => {
          event.preventDefault();
          const main = this.getMainButton();
          if (this.button.disabled || !main) return;
          main.click();
        });

        // Quantity steppers drive the page's real −/+ buttons.
        this.querySelector('[data-sticky-qty-minus]')?.addEventListener('click', () => this.stepQuantity(-1));
        this.querySelector('[data-sticky-qty-plus]')?.addEventListener('click', () => this.stepQuantity(1));

        this.observeMainPrice();
        this.observePriceVisibility();
        this.subscribeVariantImage();

        this.onResize = () => {
          cancelAnimationFrame(this.resizeRaf);
          this.resizeRaf = requestAnimationFrame(() => this.fitContents());
        };
        window.addEventListener('resize', this.onResize);

        // The real add-to-cart button (.vercony-atc-btn) is injected by the
        // theme's own script, which may run after this element upgrades. Wait
        // for it before wiring up visibility + state sync.
        this.waitForMainButton();
      }

      disconnectedCallback() {
        this.priceObserver?.disconnect();
        this.priceVisibilityObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        window.removeEventListener('resize', this.onResize);
        this.variantUnsubscribe?.();
      }

      // Keep the desktop thumbnail in sync with the selected variant image.
      // Dawn exposes `subscribe` / `PUB_SUB_EVENTS` as bare globals (not on window).
      subscribeVariantImage() {
        if (!this.image) return;
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) {
          return;
        }

        this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const src = event?.data?.variant?.featured_image?.src;
          if (!src) return;
          this.image.src = src + (src.includes('?') ? '&' : '?') + 'width=120';
        });
      }

      // Trim the row to fit one line: keep the full price if it fits, else drop
      // the struck compare price, else drop the price entirely. Measures in a
      // transient state that is applied and removed within one frame.
      fitContents() {
        if (!this.inner || !this.priceTarget) return;

        this.classList.remove('product-sticky-bar--no-compare', 'product-sticky-bar--no-price');
        this.classList.add('product-sticky-bar--measuring');

        const fits = () => this.inner.scrollWidth <= this.inner.clientWidth + 1;

        if (!fits()) {
          this.classList.add('product-sticky-bar--no-compare');
          if (!fits()) this.classList.add('product-sticky-bar--no-price');
        }

        this.classList.remove('product-sticky-bar--measuring');
      }

      // Only show the price in the bar once the on-page price is out of view,
      // so the price is never shown twice at once.
      observePriceVisibility() {
        if (!this.mainPrice || !('IntersectionObserver' in window)) return;

        this.priceVisibilityObserver = new IntersectionObserver(
          (entries) => {
            this.classList.toggle('product-sticky-bar--price-visible', !entries[0].isIntersecting);
          },
          { threshold: 0 }
        );
        this.priceVisibilityObserver.observe(this.mainPrice);
      }

      // Find the page's real add-to-cart button. This theme uses a custom
      // `.vercony-atc-btn`; fall back to the standard Dawn id / a generic add
      // button so the bar keeps working if the markup changes.
      getMainButton() {
        return (
          this.scope.querySelector('.vercony-atc-btn') ||
          document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
          this.scope.querySelector('form[action*="/cart/add"] [type="submit"], button[name="add"]')
        );
      }

      waitForMainButton(attempt = 0) {
        const main = this.getMainButton();
        if (main) {
          this.mainButton = main;
          this.anchor = main.closest('.vercony-qty-atc, .product-form__buttons') || main;
          this.syncButton();
          this.setupQuantity();
          this.observeMainButton();
          this.observeVisibility();
          this.fitContents();
          return;
        }
        if (attempt < 60) setTimeout(() => this.waitForMainButton(attempt + 1), 200);
      }

      // Wire the sticky quantity stepper to the page's real quantity control
      // (injected alongside the add button), reusing its min/max/step logic.
      setupQuantity() {
        const form = this.mainButton.closest('form') || this.scope;
        this.qtyInput = form.querySelector('input[name="quantity"], .quantity__input');

        const steppers = [...form.querySelectorAll('.vercony-qty-btn')];
        this.qtyMinus = steppers[0] || form.querySelector('.quantity__button[name="minus"]');
        this.qtyPlus = steppers[1] || form.querySelector('.quantity__button[name="plus"]');

        if (!this.qtyInput) {
          this.qtyWrap?.setAttribute('hidden', '');
          return;
        }

        this.syncQuantity();
        this.qtyInput.addEventListener('input', () => this.syncQuantity());
        this.qtyInput.addEventListener('change', () => this.syncQuantity());
      }

      stepQuantity(direction) {
        if (!this.qtyInput) return;

        if (direction < 0 && this.qtyMinus) {
          this.qtyMinus.click();
        } else if (direction > 0 && this.qtyPlus) {
          this.qtyPlus.click();
        } else {
          const step = parseInt(this.qtyInput.step, 10) || 1;
          const min = parseInt(this.qtyInput.min, 10) || 1;
          const max = parseInt(this.qtyInput.max, 10);
          let value = (parseInt(this.qtyInput.value, 10) || min) + direction * step;
          if (value < min) value = min;
          if (!Number.isNaN(max) && value > max) value = max;
          this.qtyInput.value = value;
          this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.syncQuantity();
        requestAnimationFrame(() => this.syncQuantity());
      }

      syncQuantity() {
        if (this.qtyDisplay && this.qtyInput) {
          this.qtyDisplay.textContent = this.qtyInput.value || '1';
        }
      }

      // Show the bar only while the regular add-to-cart button is off screen.
      observeVisibility() {
        if (!this.anchor || !('IntersectionObserver' in window)) return;

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            // Show the bar only once the regular add-to-cart has scrolled ABOVE
            // the viewport (i.e. the shopper scrolled down past it) — not while
            // it is still below the fold on page load.
            const topEdge = entry.rootBounds ? entry.rootBounds.top : 0;
            const show = !entry.isIntersecting && entry.boundingClientRect.bottom <= topEdge;
            this.classList.toggle('product-sticky-bar--visible', show);
            this.setAttribute('aria-hidden', show ? 'false' : 'true');
          },
          { threshold: 0 }
        );
        this.intersectionObserver.observe(this.anchor);
      }

      // Mirror the main price block so variant changes are reflected in the bar.
      observeMainPrice() {
        if (!this.mainPrice || !this.priceTarget) return;

        this.syncPrice();
        this.priceObserver = new MutationObserver(() => this.syncPrice());
        this.priceObserver.observe(this.mainPrice, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
        });
      }

      syncPrice() {
        this.priceTarget.innerHTML = this.mainPrice.innerHTML;
        this.fitContents();
      }

      // Keep the sticky button's disabled state and label in sync with the real button.
      observeMainButton() {
        if (!this.mainButton) return;

        this.buttonObserver = new MutationObserver(() => this.syncButton());
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      syncButton() {
        if (!this.mainButton || !this.button) return;

        const disabled =
          this.mainButton.hasAttribute('disabled') ||
          this.mainButton.getAttribute('aria-disabled') === 'true';
        this.button.disabled = disabled;

        const span = this.mainButton.querySelector('span');
        const text = (span ? span.textContent : this.mainButton.textContent).trim();
        if (text && this.buttonText) {
          this.buttonText.textContent = text;
          this.fitContents();
        }
      }
    }
  );
}
